/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import { applyUkgUserTimeZoneTransformation } from '@commonClient2/services/ukgDateTime';
import AuthService from '@commonClient2/services/auth';
import {
    UPDATE_EMPLOYEE_PREFERENCES_REPORT,
} from './employeePreferencesReport.actions';
import { LOADING_END, LOADING_START } from '../loading';
import * as apiManager from '../../Manager/services/api-endpoints';
import * as apiEmployee from '../../Employee/services/api-endpoints';
import FileService from '../../services/FileService';
import DateFormatService from '../../services/DateFormatService';
import { getScheduledTime } from '../../services/bidProfile';
import { UkgDateFormat } from '../../Enums';
import i18n from '../../services/i18n';


/**
 * @param {string} profileId
 * @param {BidProfile.ISequentialEmployeeSchedule?} sequentialSchedule
 * @param {EmployeePreferencesReport.IEmployeeReportFilters} filters
 * @param {EmployeePreferencesReport.IPagination} pagination
 * @param {EmployeePreferencesReport.ISortParameters} sortParameters
 */
// eslint-disable-next-line import/prefer-default-export
export const loadEmployeePreferences = (profileId, sequentialSchedule, filters, pagination, sortParameters) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = AuthService.isManager()
            ? await apiManager.EmployeesSelection(profileId, filters, pagination, sortParameters)
            : await apiEmployee.EmployeesSelection(profileId, filters, pagination, sortParameters);
        if (result.status === 200) {
            const stringifySubmittedOn = (/** @type {Date} */ date) => {
                const submittedOnDate = applyUkgUserTimeZoneTransformation(date);
                return DateFormatService.formatDateOrEmpty(submittedOnDate, UkgDateFormat.LongDateTime);
            };
            /** @type {EmployeePreferencesReport.IEmployeePreference[]} */
            const employeePreferences = result.data.employeeSelections.map((ee) => {
                const submittedOn = stringifySubmittedOn(ee.submittedOn);
                const scheduledDateAndTime = sequentialSchedule ? getScheduledTime(sequentialSchedule[ee.employeeId], sequentialSchedule) : '';
                return {
                    employeeId: ee.employeeId,
                    name: ee.employeeName,
                    rank: ee.rank,
                    preference: ee.preference,
                    scheduleTemplateName: ee.scheduleTemplateName,
                    isAwarded: ee.isAwarded,
                    quantity: ee.quantity,
                    awardedScheduleName: ee.awardedScheduleName,
                    currentShiftLineAssignment: ee.currentShiftLineAssignment,
                    scheduledDateAndTime,
                    submittedOn,
                };
            });
            return dispatch({
                type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
                payload: {
                    employeePreferences,
                    pagination: {
                        ...pagination,
                        totalCount: result.data.totalCount,
                        pageNumber: Math.min(Math.max(pagination.pageNumber, 1), Math.ceil(result.data.totalCount / pagination.pageSize)),
                    },
                },
            });
        }
        toastr.error(i18n.t('shiftBidding:toastr.fetchingEmployeePreferencesReport'), i18n.t('shiftBidding:toastr.failedToFetchEmployeePreferencesReport'));
        return Promise.reject();
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.fetchingEmployeePreferencesReport'), error.response.data.Message);
        return Promise.reject(error);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {BidProfile.ISequentialEmployeeSchedule?} sequentialSchedule
 * @param {EmployeePreferencesReport.IEmployeeReportFilters} filters
 * @param {EmployeePreferencesReport.IPagination} pagination
 * @param {number} pageNumber
 */
// eslint-disable-next-line import/prefer-default-export
export const handleEmployeePreferencesPageChange = (profileId, sequentialSchedule, filters, pagination, pageNumber) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = AuthService.isManager()
            ? await apiManager.EmployeesSelection(profileId, filters, pagination)
            : await apiEmployee.EmployeesSelection(profileId, filters, pagination);
        if (result.status === 200) {
            const stringifySubmittedOn = (/** @type {Date} */ date) => {
                const submittedOnDate = applyUkgUserTimeZoneTransformation(date);
                return DateFormatService.formatDateOrEmpty(submittedOnDate, UkgDateFormat.LongDateTime);
            };
            /** @type {EmployeePreferencesReport.IEmployeePreference[]} */
            const employeePreferences = result.data.employeeSelections.map((ee) => {
                const submittedOn = stringifySubmittedOn(ee.submittedOn);
                const scheduledDateAndTime = sequentialSchedule ? getScheduledTime(sequentialSchedule[ee.employeeId], sequentialSchedule) : '';
                return {
                    employeeId: ee.employeeId,
                    name: ee.employeeName,
                    rank: ee.rank,
                    preference: ee.preference,
                    scheduleTemplateName: ee.scheduleTemplateName,
                    isAwarded: ee.isAwarded,
                    quantity: ee.quantity,
                    awardedScheduleName: ee.awardedScheduleName,
                    currentShiftLineAssignment: ee.currentShiftLineAssignment,
                    scheduledDateAndTime,
                    submittedOn,
                };
            });
            return dispatch({
                type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
                payload: {
                    employeePreferences,
                    pagination: {
                        totalCount: result.data.totalCount,
                        pageIndex: pageNumber,
                    },
                },
            });
        }
        toastr.error(i18n.t('shiftBidding:toastr.fetchingEmployeePreferencesReport'), i18n.t('shiftBidding:toastr.failedToFetchEmployeePreferencesReport'));
        return Promise.reject();
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.fetchingEmployeePreferencesReport'), error.response.data.Message);
        return Promise.reject(error);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {number[]} employeeIds
 * @param {string[]} selectedDataviews
 * @param {string} searchQuery
 */
export const downloadReportAsCsv = (profileId, employeeIds, selectedDataviews, searchQuery) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await apiManager.DownloadEmployeeSelectionsCsv(profileId, employeeIds, selectedDataviews, searchQuery);

        if (response.status === 200) {
            FileService.downloadContentInFile('EmployeeSelection', 'csv', response.data);
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.downloadingCSVFile'), i18n.t('shiftBidding:toastr.failedToDownload'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.downloadingCSVFile'), i18n.t('shiftBidding:toastr.failedToDownload'));
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
